import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { DayPicker } from '@daypicker/react';
import type { DateRange } from '@daypicker/react';
import daypickerClassNames from '@daypicker/react/style.module.css';
import styles from './booking-widget.module.scss';
import calendarStyles from './calendar-styles.module.scss';
import { parseISODate, isWithin } from '@lib/functions/dateTimeFunctions';

// Merge our local override class onto the picker's own hashed `root` class so
// both apply to the same root element (see calendar-styles.module.scss).
const dayPickerClassNames = {
    ...daypickerClassNames,
    root: `${daypickerClassNames.root} ${calendarStyles.root}`,
};

const BookingCalendar = ({
    open,
    setOpen,
    dateRange,
    setRangeFunc,
    dateRangeStrings,
    isPopup = false,
}: {
    open: boolean;
    setOpen: (v: boolean) => void;
    dateRange: DateRange | undefined;
    setRangeFunc: (range: DateRange | undefined) => void;
    dateRangeStrings: { from: string; to: string };
    isPopup?: boolean;
}) => {
    const [isMounted, setIsMounted] = useState(false);
    const [dateBounds, setDateBounds] = useState<{
        from: Date;
        to: Date;
    } | null>(null);

    // Local, freely-editable text so users can type partial values mid-edit.
    const [fromText, setFromText] = useState(dateRangeStrings.from);
    const [toText, setToText] = useState(dateRangeStrings.to);
    const [error, setError] = useState('');

    // Current month displayed in calendar
    const [month, setMonth] = useState<Date | undefined>(dateRange?.from);

    const dialogRef = useRef<HTMLDivElement>(null);
    const fromInputRef = useRef<HTMLInputElement>(null);

    // Stable, unique ids so labels/hints/errors associate correctly.
    const baseId = useId();
    const fromId = `${baseId}-from`;
    const toId = `${baseId}-to`;
    const dialogId = `${baseId}-dialog`;
    const hintId = `${baseId}-hint`;
    const errorId = `${baseId}-error`;

    useEffect(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxDate = new Date(today);
        maxDate.setDate(today.getDate() + 365);

        setDateBounds({ from: today, to: maxDate });
        setIsMounted(true);
    }, []);

    // Reflect calendar-driven changes back into the text fields.
    useEffect(
        () => setFromText(dateRangeStrings.from),
        [dateRangeStrings.from],
    );
    useEffect(() => setToText(dateRangeStrings.to), [dateRangeStrings.to]);

    // Validate + commit typed values to the shared range.
    const commit = useCallback(
        (rawFrom: string, rawTo: string) => {
            if (!dateBounds) return;
            const from = parseISODate(rawFrom);
            const to = parseISODate(rawTo);

            if (rawFrom && !from) {
                setError('Enter the check-in date as YYYY-MM-DD.');
                return;
            }
            if (rawTo && !to) {
                setError('Enter the check-out date as YYYY-MM-DD.');
                return;
            }
            if (from && !isWithin(from, dateBounds.from, dateBounds.to)) {
                setError(
                    'Check-in must be between today and one year from now.',
                );
                return;
            }
            if (to && !isWithin(to, dateBounds.from, dateBounds.to)) {
                setError(
                    'Check-out must be between today and one year from now.',
                );
                return;
            }
            if (from && to && to.getTime() < from.getTime()) {
                setError('Check-out must be on or after check-in.');
                return;
            }

            setError('');
            if (from) setMonth(from);
            else if (to) setMonth(to);
            setRangeFunc(from || to ? { from, to } : undefined);
        },
        [dateBounds, setRangeFunc],
    );

    const openCalendar = useCallback(() => {
        if (!open) setOpen(true);
    }, [open, setOpen]);

    const closeCalendar = useCallback(() => {
        setOpen(false);
        fromInputRef.current?.focus();
    }, [setOpen]);

    // Move focus into the calendar when it opens.
    useEffect(() => {
        if (open) dialogRef.current?.focus();
    }, [open]);

    const handleFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commit(fromText, toText);
        }
        if (e.key === 'Escape' && open) {
            setOpen(false);
        }
    };
    const describedBy = `${hintId}${error ? ` ${errorId}` : ''}`;

    return (
        <div className={`${styles.fieldWrapper} ${styles.inputWrapper}`}>
            {open && isMounted && dateBounds && (
                <div
                    id={dialogId}
                    ref={dialogRef}
                    role="dialog"
                    aria-label="Choose your stay dates"
                    tabIndex={-1}
                    className={`${styles.selectorOpen} ${styles.selector} ${styles.scaler}`}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.stopPropagation();
                            closeCalendar();
                        }
                    }}
                >
                    <div className={styles.wrapper}>
                        <p
                            id={hintId}
                            className={`${styles.hint} ${isPopup ? styles.popupHint : ''}`}
                        >
                            Format: YYYY-MM-DD
                        </p>

                        {/* Error is always in the DOM so assistive tech announces it when it appears */}
                        <p id={errorId} className={styles.error} role="alert">
                            {error}
                        </p>
                        <DayPicker
                            mode="range"
                            startMonth={dateBounds.from}
                            endMonth={dateBounds.to}
                            disabled={{
                                before: dateBounds.from,
                                after: dateBounds.to,
                            }}
                            selected={dateRange}
                            month={month}
                            onMonthChange={setMonth}
                            onSelect={(range) => {
                                setError('');
                                setRangeFunc(range);
                            }}
                            classNames={dayPickerClassNames}
                            showOutsideDays
                        />
                    </div>
                </div>
            )}
            <div
                className={styles.fieldGroup}
                role="group"
                aria-label="Stay dates"
            >
                <div className={`${styles.field} ${styles.groupHalf}`}>
                    <label htmlFor={fromId} className={styles.label}>
                        From:
                    </label>
                    <input
                        id={fromId}
                        ref={fromInputRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="YYYY-MM-DD"
                        className={`${styles.field} ${styles.input} rooms-guest`}
                        value={fromText}
                        role="combobox"
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        aria-controls={dialogId}
                        aria-describedby={describedBy}
                        aria-invalid={error ? true : undefined}
                        onFocus={openCalendar}
                        onChange={(e) => setFromText(e.target.value)}
                        onBlur={() => commit(fromText, toText)}
                        onKeyDown={handleFieldKeyDown}
                    />
                </div>

                <div className={styles.fieldSeparator} />

                <div className={`${styles.field} ${styles.groupHalf}`}>
                    <label htmlFor={toId} className={styles.label}>
                        To:
                    </label>
                    <input
                        id={toId}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="YYYY-MM-DD"
                        className={`${styles.field} ${styles.input} rooms-guest`}
                        value={toText}
                        role="combobox"
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        aria-controls={dialogId}
                        aria-describedby={describedBy}
                        aria-invalid={error ? true : undefined}
                        onFocus={openCalendar}
                        onChange={(e) => setToText(e.target.value)}
                        onBlur={() => commit(fromText, toText)}
                        onKeyDown={handleFieldKeyDown}
                    />
                </div>
            </div>
        </div>
    );
};

export default BookingCalendar;
